import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  Target,
  Heart,
  Award,
  Users,
  Shield,
  Zap
} from 'lucide-react';
import { getValues, addValue, updateValue, deleteValue } from '../../utils/contentManager';

const ValuesManagement = () => {
  const [values, setValues] = useState([]);
  const [editingValue, setEditingValue] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: 'Target'
  });
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const availableIcons = [
    { name: 'Target', icon: Target },
    { name: 'Heart', icon: Heart },
    { name: 'Award', icon: Award },
    { name: 'Users', icon: Users },
    { name: 'Shield', icon: Shield },
    { name: 'Zap', icon: Zap }
  ];

  useEffect(() => {
    loadValues();
  }, []);

  const loadValues = () => {
    const valuesData = getValues();
    setValues(valuesData);
  };

  const handleAdd = () => {
    setShowAddForm(true);
    setEditingValue(null);
    setFormData({
      title: '',
      description: '',
      icon: 'Target'
    });
  };

  const handleEdit = (value) => {
    setEditingValue(value.id);
    setShowAddForm(true);
    setFormData({
      title: value.title,
      description: value.description,
      icon: value.icon || 'Target'
    });
  };

  const handleSave = () => {
    try {
      if (!formData.title.trim() || !formData.description.trim()) {
        setError('Title and description are required');
        return;
      }

      if (editingValue) {
        const result = updateValue(editingValue, formData);
        result ? setSuccess('Value updated successfully') : setError('Failed to update value');
      } else {
        const result = addValue(formData);
        result ? setSuccess('Value added successfully') : setError('Failed to add value');
      }

      loadValues();
      handleCancel();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save value');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this value?')) {
      try {
        const result = deleteValue(id);
        if (result) {
          setSuccess('Value deleted successfully');
          loadValues();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete value');
          setTimeout(() => setError(''), 3000);
        }
      } catch (err) {
        console.error('Delete error:', err);
        setError('Failed to delete value');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingValue(null);
    setError('');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getIconComponent = (iconName) => {
    const iconObj = availableIcons.find(i => i.name === iconName);
    return iconObj ? iconObj.icon : Target;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Values Management</h1>
          <p className="text-gray-600 mt-2">Manage your company values</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleAdd}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg 
          hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Add Value</span>
        </motion.button>
      </div>

      {/* Alerts */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3"
          >
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-700">{error}</span>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3"
          >
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-700">{success}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add/Edit Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingValue ? 'Edit Value' : 'Add New Value'}
              </h3>
              <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Value title"
                />
              </div>

              {/* Icon */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
                <select
                  value={formData.icon}
                  onChange={(e) => handleInputChange('icon', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {availableIcons.map((icon) => (
                    <option key={icon.name} value={icon.name}>
                      {icon.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Value description"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-4 mt-6">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSave}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 
                transition-colors duration-200 flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{editingValue ? 'Update' : 'Save'}</span>
              </motion.button>
              <button
                onClick={handleCancel}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg 
                hover:bg-gray-400 transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Values List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {values.map((value) => {
          const IconComponent = getIconComponent(value.icon);
          return (
            <motion.div
              key={value.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <IconComponent className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEdit(value)}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(value.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
              <p className="text-gray-600">{value.description}</p>
            </motion.div>
          );
        })}
      </div>

      {values.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Plus className="w-12 h-12 mx-auto" />
          </div>
          <p className="text-gray-500 mb-4">No values found</p>
          <button
            onClick={handleAdd}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Add your first value
          </button>
        </div>
      )}
    </div>
  );
};

export default ValuesManagement;
